import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IMailrailDripStep, MailrailDripEmailCampaign } from '@feathr/blackbox';
import { MailrailDripStep } from '@feathr/blackbox';
import { Button, CardV2 as Card, ConfirmModal, InlineEdit, StepSection } from '@feathr/components';

import MailrailTestSendForm from '../MailrailTestSendForm';
import DripStepCardEditor from './DripStepCardEditor';
import DripStepCardPreview from './DripStepCardPreview';
import type { validateStepDripBuilder } from './MailrailCampaignStepDripBuilder';
import type { IStepLock } from './MailrailCampaignStepDripBuilder.hooks';

import * as styles from './DripStepCard.css';

interface IDripStepCardProps {
  campaign: MailrailDripEmailCampaign;
  step: IMailrailDripStep;
  /** The step's index in the embedded steps array (0-based) */
  index: number;
  disabled: boolean;
  editingStep: number | null;
  /** When true, the card is collapsed and its drag handle is enabled */
  isReordering: boolean;
  lock: IStepLock;
  defaultOpen: boolean;
  /** Whether the card's StepSection header shows the complete (check) vs incomplete (minus) icon. */
  isComplete: boolean;
  validationErrors: ReturnType<typeof validateStepDripBuilder>['stepErrors'][number];
  onChange: (index: number, patch: Partial<IMailrailDripStep>) => void;
  onDone: () => void;
  onEdit: (cardIndex: number) => void;
  onDelete: (index: number) => void;
  onRename: (index: number, name?: string) => void;
}

/**
 * A single sortable embedded-step card (cards 1..N). Kept as its own component so the
 * `useSortable` hook (which must run unconditionally per item) is called at the top level.
 *
 * @param props - {@link IDripStepCardProps}.
 */
function DripStepCard({
  campaign,
  step,
  index,
  disabled,
  editingStep,
  isReordering,
  lock,
  defaultOpen,
  isComplete,
  validationErrors,
  onChange,
  onDone,
  onEdit,
  onDelete,
  onRename,
}: Readonly<IDripStepCardProps>): JSX.Element {
  const { t } = useTranslation();
  const cardIndex = index + 1;
  // Wrap the plain step in a model so the preview has `.get()`.
  const stepModel = useMemo(() => new MailrailDripStep(step), [step]);
  // Handles + sorting are only active in reorder mode.
  const canDrag = lock.draggable && !disabled && isReordering;
  const showsDelete = !disabled && editingStep === null && !isReordering;
  const canDelete = showsDelete && lock.removable;
  // Once a drip has run, some steps can be deleted and others can't (see getStepLockState), so the
  // ones that can't show a disabled delete button that says why rather than no button at all.
  const deleteDisabledReason =
    showsDelete && !lock.removable && (campaign.isStopped || campaign.isComplete)
      ? t('Steps that have already run can only be deleted from the end of the drip')
      : undefined;
  const [testOpened, setTestOpened] = useState(false);
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: step.step_id,
    disabled: disabled || !lock.draggable || !isReordering,
  });

  // Pinned (non-draggable) cards opt out of the sortable transform so they stay put when another
  // card is dragged over them. They can never be reordered anyway (handleDragEnd refuses to move a
  // step to or above a pinned one), so animating them aside only to snap back is misleading.
  const style = {
    transform: lock.draggable ? CSS.Transform.toString(transform) : undefined,
    transition: lock.draggable ? transition : undefined,
  };
  const dragProps = canDrag ? { ...attributes, ...listeners, 'aria-label': t('Reorder step') } : {};

  function handleChange(patch: Partial<IMailrailDripStep>): void {
    onChange(index, patch);
  }

  function handleEdit(): void {
    onEdit(cardIndex);
  }

  function handleDelete(): void {
    openDeleteModal();
  }

  function confirmDelete(): void {
    onDelete(index);
    closeDeleteModal();
  }

  function handleRename(value?: string): void {
    onRename(index, value);
  }

  function handleOpenTest(): void {
    setTestOpened(true);
  }

  function handleCloseTest(): void {
    setTestOpened(false);
  }

  return (
    <>
      <StepSection
        className={canDrag ? styles.draggableCard : undefined}
        collapsed={isReordering}
        defaultOpen={defaultOpen}
        deleteDisabledReason={deleteDisabledReason}
        disabled={disabled}
        editing={editingStep === cardIndex}
        isComplete={isComplete}
        name={`step-${cardIndex + 1}`}
        onDelete={canDelete ? handleDelete : undefined}
        ref={setNodeRef}
        stepIndex={cardIndex}
        stepNumber={cardIndex + 1}
        style={style}
        {...dragProps}
      >
        <StepSection.ReorderableHeader draggable={canDrag} reordering={isReordering}>
          <InlineEdit
            disabled={disabled || isReordering}
            onChange={handleRename}
            validationError={step.name === '' ? t("Step name can't be blank") : undefined}
            value={step.name}
          />
        </StepSection.ReorderableHeader>
        <StepSection.Content>
          {editingStep === cardIndex ? (
            <DripStepCardEditor
              campaign={campaign}
              disabled={disabled}
              onChange={handleChange}
              onDone={onDone}
              step={step}
              validationErrors={validationErrors}
            />
          ) : (
            <Card theme={'disabled'} width={'full'}>
              <DripStepCardPreview campaign={campaign} mailing={stepModel} />
              <Card.Actions>
                <Button
                  disabled={disabled || editingStep !== null || isReordering}
                  name={`step-${cardIndex + 1}-edit`}
                  onClick={handleEdit}
                >
                  {t('Edit step')}
                </Button>
                <Button
                  disabled={disabled || editingStep !== null || isReordering}
                  name={`step-${cardIndex + 1}-test`}
                  onClick={handleOpenTest}
                  type={'secondary'}
                >
                  {t('Send test email')}
                </Button>
              </Card.Actions>
              <MailrailTestSendForm
                campaign={campaign}
                mailing={stepModel}
                onClose={handleCloseTest}
                opened={testOpened}
                variant={'modal'}
              />
            </Card>
          )}
        </StepSection.Content>
      </StepSection>
      <ConfirmModal
        cancelButtonText={t('Cancel')}
        confirmButtonText={t('Delete')}
        confirmButtonType={'danger'}
        description={t('This step will be removed from the drip.')}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        opened={isDeleteModalOpen}
        t={t}
        title={t('Delete this step?')}
      />
    </>
  );
}

export default observer(DripStepCard);
